import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/* eslint-disable no-nested-ternary */
import React from 'react';
/**
 * Reconstructs the original HCPCS UI state from saved API answers
 * This reverses the buildHCPCS process to show users what they originally entered
 */

/**
 * Find which HCPCS menu item matches a given code
 */
const findHCPCSMenuItem = (key, hcpcsMenu) => {
  let result = null;
  hcpcsMenu.some(item => {
    if (item.key === key) {
      result = item;
      return true;
    } // Check submenu if exists


    if (item.submenu) {
      const found = findHCPCSMenuItem(key, item.submenu);

      if (found) {
        result = found;
        return true;
      }
    }

    return false;
  });
  return result;
};
/**
 * Group answers by their HCPCS key/type
 */


const groupAnswersByKey = (answers, hcpcsMenu) => {
  const grouped = new Map();
  answers.forEach(answer => {
    const keyToFind = answer.key;
    const menuItem = findHCPCSMenuItem(keyToFind, hcpcsMenu);
    if (!menuItem) return;
    const {
      key
    } = menuItem;

    if (!grouped.has(key)) {
      grouped.set(key, {
        codes: [],
        duration: answer.duration,
        diagnoses: new Set(),
        menuItem
      });
    }

    const group = grouped.get(key);

    if (group) {
      group.codes.push(answer.hcpcsCode); // Add ICD codes

      answer.hcpcsDiagnoses.forEach(icd => group.diagnoses.add(icd));
    }
  }); // Convert Set to Array for diagnoses

  const result = new Map();
  grouped.forEach((value, key) => {
    result.set(key, {
      codes: value.codes,
      duration: value.duration,
      diagnoses: Array.from(value.diagnoses),
      menuItem: value.menuItem
    });
  });
  return result;
};
/**
 * Main function to reconstruct the UI state from API answers
 */


export const reconstructHCPCSFromAnswers = (answers, hcpcsMenu) => {
  const grouped = groupAnswersByKey(answers, hcpcsMenu);
  const hcpcsChoices = [];
  const hcpcsDuration = {};
  const icdCodes = {};
  const icdCodeNames = {};
  const icdSection = {}; // Use Object.entries instead of Map iteration

  grouped.forEach((group, key) => {
    const {
      codes,
      duration,
      diagnoses,
      menuItem
    } = group; // Add to choices (mark as visible for UI)

    hcpcsChoices.push(_objectSpread(_objectSpread({}, menuItem), {}, {
      visible: true
    })); // Calculate duration

    hcpcsDuration[key] = duration; // Reconstruct ICD codes

    if (diagnoses.length > 0) {
      icdCodes[key] = diagnoses.reduce((acc, icd) => {
        acc[icd] = true;
        return acc;
      }, {});
      icdCodeNames[key] = diagnoses; // Build icdSection - one entry per ICD code with the menuItem

      icdSection[key] = diagnoses.map(() => _objectSpread(_objectSpread({}, menuItem), {}, {
        hide: false
      }));
    } else {
      icdCodes[key] = {};
      icdCodeNames[key] = [];
      icdSection[key] = [];
    }
  });
  return {
    hcpcsChoices,
    hcpcsDuration,
    icdCodes,
    icdCodeNames,
    icdSection
  };
};
const LOAD_SAVED_HCPCS_DEFAULT_DEPS = [];

/**
 * React Hook to load saved HCPCS data into the context
 */
export const useLoadSavedHCPCS = (answers, dispatchHCPCS, hcpcsMenu, SET_HCPCS_CHOICE, SET_HCPCS_DURATION, SET_ICD_SECTION, ADD_SAVED_ICD_CODE, options) => {
  const {
    resetType,
    deps = LOAD_SAVED_HCPCS_DEFAULT_DEPS
  } = options || {};
  React.useEffect(() => {
    if (!answers || !answers.length) return; // Optional: clear existing HCPCS form state before replaying saved answers. Dashboard HCPCS
    // context can outlive the dialog, so without this, new loads merge with stale choices/ICDs.
    // Use a reset that does NOT clear `showHCPCS` (e.g. RESET_HCPCS_FOR_RELOAD), not full RESET_HCPCS.
    // Visit-log omits `resetType`; mount is typically a clean context, so this block is skipped.

    if (resetType) {
      dispatchHCPCS({
        type: resetType
      });
    }

    const reconstructed = reconstructHCPCSFromAnswers(answers, hcpcsMenu); // console.log('reconstructed', reconstructed);
    // Dispatch actions to populate the context

    reconstructed.hcpcsChoices.forEach(choice => {
      dispatchHCPCS({
        type: SET_HCPCS_CHOICE,
        payload: choice
      });
    });
    Object.entries(reconstructed.hcpcsDuration).forEach(([key, duration]) => {
      dispatchHCPCS({
        type: SET_HCPCS_DURATION,
        payload: {
          key,
          duration
        }
      });
    }); // Set icdSection first (creates the ICD input boxes)

    Object.entries(reconstructed.icdSection).forEach(([key, codes]) => {
      dispatchHCPCS({
        type: SET_ICD_SECTION,
        payload: {
          key,
          codes
        }
      });
    }); // Then populate the actual ICD codes

    Object.entries(reconstructed.icdCodes).forEach(([key, codes]) => {
      Object.keys(codes).forEach(icdCode => {
        dispatchHCPCS({
          type: ADD_SAVED_ICD_CODE,
          payload: {
            key,
            icdCode
          }
        });
      });
    }); // todo careful only on Load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};